import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Pill,
  Post,
  TopicsSection,
  PostCreator,
  SegmentedControl,
  Spinner,
  CommunityGuidelinesLink,
} from "../components/index.js";
import { LeftArrowCircleIcon } from "../assets/icons/index.js";
import topics from "../data/topics.js";
import requestHandler from "../utils/requestHandler.js";

const filters = [
  {
    name: "recent",
    label: "Recent",
  },
  {
    name: "popular",
    label: "Popular",
  },
];

export default function Community() {
  const location = useLocation();
  const [selectedFilter, setSelectedFilter] = useState("recent");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [mobileView, setMobileView] = useState("recent");

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);

      const params = {
        filter: selectedFilter === "recent" ? 1 : 2,
      };

      if (selectedTopic) {
        params.topic = selectedTopic;
      }

      const res = await requestHandler({
        path: "posts",
        method: "GET",
        params,
      });

      if (res.success) {
        setPosts(res.data);
      } else {
        console.error("Error loading posts:", res.error);
      }
      setLoading(false);
    };

    loadPosts();
  }, [selectedFilter, selectedTopic]);

  // Check for navigation state and set selected topic
  useEffect(() => {
    if (location.state?.selectedTopic) {
      setSelectedTopic(location.state.selectedTopic);
      // On mobile, switch to topics view when a topic is selected
      if (window.innerWidth < 768) {
        setMobileView("topics");
      }
      // Clear the state to prevent it from persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const mobileOptions = [
    { name: "recent", label: "Recent" },
    { name: "popular", label: "Popular" },
    { name: "topics", label: "Topics" },
  ];

  const handleMobileViewChange = (view) => {
    setMobileView(view);
    if (view === "recent" || view === "popular") {
      setSelectedFilter(view);
    }
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] md:h-[calc(100vh-4rem)] flex-row justify-between gap-4 pb-4">
      {/* Posts Section */}
      <div className="flex flex-col gap-4 h-full overflow-y-auto no-scrollbar w-full md:w-3/4">
        <h1 className="text-4xl uppercase text-lz-candle-dim dark:text-lz-concrete">
          <div className="flex flex-row gap-2 items-center">
            {selectedTopic && (
              <button
                onClick={() => {
                  setSelectedTopic("");
                  setMobileView("recent");
                }}
                aria-label="Back to all topics"
              >
                <LeftArrowCircleIcon
                  className="dark:fill-lz-concrete w-10 h-10 cursor-pointer"
                />
              </button>
            )}
            Community
          </div>
        </h1>
        {selectedTopic && (
          <>
            <span className="text-2xl text-lz-ember dark:text-lz-candle-dim">
              {topics.find((topic) => topic.index === selectedTopic)?.label}
            </span>

            <span className="text-lg text-lz-ink-mid dark:text-lz-concrete">
              {
                topics.find((topic) => topic.index === selectedTopic)
                  ?.description
              }
            </span>
          </>
        )}
        <PostCreator initialTopic={selectedTopic} />

        {/* Mobile Segmented Control */}
        <div className="md:hidden">
          <SegmentedControl
            options={mobileOptions}
            selected={mobileView}
            onChange={handleMobileViewChange}
          />
        </div>

        {/* Desktop Pills */}
        <div className="hidden md:flex flex-row gap-4">
          <div className="flex flex-row gap-4">
            {filters.map((filter) => (
              <Pill
                selected={selectedFilter === filter.name}
                onClick={() => setSelectedFilter(filter.name)}
                key={filter.name}
              >
                {filter.label}
              </Pill>
            ))}
          </div>
        </div>

        {/* Topics Pills Carousel - visible when topics tab is selected */}
        {mobileView === "topics" && (
          <div className="md:hidden w-full">
            <div className="overflow-x-auto no-scrollbar pb-2">
              <div className="flex flex-row gap-2 w-max">
                {topics.map((topic) => (
                  <Pill
                    key={topic.index}
                    selected={selectedTopic === topic.index}
                    onClick={() => {
                      setSelectedTopic(topic.index);
                    }}
                    className="whitespace-nowrap text-sm"
                  >
                    {topic.label}
                  </Pill>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Community guidelines link - mobile only; renders under the topic tags when shown */}
        <CommunityGuidelinesLink className="md:hidden" />

        {/* Posts */}
        <div className="flex flex-col gap-4">
          {loading ? (
            <Spinner />
          ) : (
            posts.map((post) => (
              <Post key={post.id} post={post} disableCommentInput />
            ))
          )}
        </div>
      </div>

      {/* Desktop Topics Section - Always visible on desktop */}
      <TopicsSection
        onTopicClick={setSelectedTopic}
        topics={topics}
        selectedTopic={selectedTopic}
        className="hidden md:block"
        showGuidelines
      />
    </div>
  );
}
